// src/components/LimiterList.jsx
import React, { useEffect, useState, useContext } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    List,
    IconButton,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
} from '@mui/material';
import { Edit as EditIcon,  } from '@mui/icons-material';
import {http,ConfigContext} from '../../../../../base-ui/lib';
import { toastError, toastSuccess } from '../../../../../Toaster/Toast';

const LimiterList = () => {
    const { config } = useContext(ConfigContext);
    const [limiters, setLimiters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for Adding Limiter
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newItemName, setNewItemName] = useState('');
    const [newLimiterType, setNewLimiterType] = useState('dropdown');
    const [newLimiterValues, setNewLimiterValues] = useState('');

    // State for Editing Limiter
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingLimiter, setEditingLimiter] = useState(null);

    // Fetch all limiters on component mount
    useEffect(() => {
        fetchLimiters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchLimiters = async () => {
        setLoading(true);
        try {
            const response = await http.get(
                { pageSize: 100, pageOffset: 0, sortBy: 'name', sortDirection: 'asc' },
                `${config.uri.base}/limiters`
            );
            setLimiters(response.rows || response.data); // Adjust based on your API response structure
        } catch (error) {
            console.error('Error fetching limiters:', error);
            setError('Error fetching Limiters.');
        } finally {
            setLoading(false);
        }
    };

    // #Feature to be implmented in the future
    // Handle Delete Limiter
    // const handleDelete = async (id) => {
    //     if (!window.confirm('Are you sure you want to delete this Limiter?')) return;
    //     try {
    //         await http.delete({}, `${config.uri.base}/limiters/${id}`);
    //         setLimiters(limiters.filter((limiter) => limiter._id !== id));
    //         SuccessToast('Limiter deleted successfully.')
    //     } catch (error) {
    //         console.error('Error deleting limiter:', error);
    //         ErrorToast('Error deleting Limiter.')
    //     }
    // };

    // Handle Open Edit Dialog
    const handleEditClick = (limiter) => {
        setEditingLimiter(limiter);
        setEditDialogOpen(true);
    };

    // Handle Save Edited Limiter
    const handleEditSave = async () => {
        if (!editingLimiter.name.trim()) {
            return toastError({
                title: 'Validation Error',
                description: 'Limiter name is required.'
            })
        }

        const payload = {
            name: editingLimiter.name.trim(),
            type: editingLimiter.type,
        };

        if (editingLimiter.type === 'dropdown') {
            // Convert comma-separated values to array
            payload.values = editingLimiter.values;
            delete payload.value;
        } else if (editingLimiter.type === 'numeric') {
            payload.value = parseFloat(editingLimiter.value);
            delete payload.values;
        }

        try {
            const response = await http.put(payload, `${config.uri.base}/limiters/${editingLimiter._id}`);
            if (response.errored) {
                return toastError({
                    title: 'Error',
                    description: response.error.message
                })
            }
            
            setLimiters(limiters.map((lim) => (lim._id === response._id ? response : lim)));
            toastSuccess({
                title: 'Limiter updated successfully.',
                description: `${response.name} has been updated.`
            })
        } catch (error) {
            console.error('Error updating limiter:', error);
            toastError({
                title: 'Error',
                description: 'An error occurred while updating the Limiter.'
            })
        } finally {
            setEditDialogOpen(false);
            setEditingLimiter(null);
        }
    };

    // Handle Save New Limiter
    const handleAddSave = async () => {
        if (!newItemName.trim()) {
            toastError({
                title: 'Validation Error',
                description: 'Limiter name is required.'
            })
            return;
        }

        const payload = {
            name: newItemName.trim(),
            type: newLimiterType,
        };

        if (newLimiterType === 'dropdown') {
            // Convert comma-separated values to array
            payload.values = newLimiterValues.split(',').map((v) => v.trim());
        } else if (newLimiterType === 'numeric') {
            payload.value = parseFloat(newLimiterValues);
        }

        try {
            const response = await http.post(payload, `${config.uri.base}/limiters`);
            if (response.errored) {
                toastError({
                    title: 'Error',
                    description: response.error.message
                })
            }

            setLimiters([...limiters, response]);
            toastSuccess({
                title: 'Limiter added successfully.',
                description: `${response.name} has been added to the list.`
            })
        } catch (error) {
            console.error('Error adding limiter:', error);
            toastError({
                title: 'Error',
                description: 'An error occurred while adding the Limiter.'
            })
        } finally {
            setAddDialogOpen(false);
            setNewItemName('');
            setNewLimiterType('dropdown');
            setNewLimiterValues('');
        }
    };

    if (loading)
        return (
            <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress size={24} />
            </Box>
        );

    return (
        <Box sx={{ mb: 4 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Typography variant="h6">Manage Limiters</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setAddDialogOpen(true)}
                >
                    Add Limiter
                </Button>
            </Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {limiters.length === 0 ? (
                <Typography variant="body1">No Limiters found.</Typography>
            ) : (
                <List sx={{ padding: 0 }}>
                    {limiters.map((limiter) => (
                        <Card key={limiter._id} variant="outlined" sx={{ my: 1 }}>
                            <CardContent>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Box>
                                        <Typography variant="h6">{limiter.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Type: {limiter.type === 'dropdown' ? 'Dropdown List' : 'Numeric Field'}
                                        </Typography>
                                        {limiter.type === 'dropdown' && (
                                            <Typography variant="body2" color="text.secondary">
                                                Values: {limiter.values.join(', ')}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleEditClick(limiter)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        {/* FEATURE TO BE USED IN FUTURE */} 
                                        {/* <IconButton
                                            color="error"
                                            onClick={() => handleDelete(limiter._id)}
                                        >
                                            <DeleteIcon />
                                        </IconButton> */}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </List>
            )}

            {/* Add Limiter Dialog */}
            <Dialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Add Limiter</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            label="Name"
                            variant="outlined"
                            fullWidth
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                            <InputLabel id="new-limiter-type-label">Type</InputLabel>
                            <Select
                                labelId="new-limiter-type-label"
                                value={newLimiterType}
                                onChange={(e) => setNewLimiterType(e.target.value)}
                                label="Type"
                            >
                                <MenuItem value="dropdown">Dropdown List</MenuItem>
                                <MenuItem value="numeric">Numeric Field</MenuItem>
                            </Select>
                        </FormControl>
                        {newLimiterType === 'dropdown' && (
                            <TextField
                                label="Values (comma separated)"
                                variant="outlined"
                                fullWidth
                                value={newLimiterValues}
                                onChange={(e) => setNewLimiterValues(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleAddSave} variant="contained" color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Limiter Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setEditingLimiter(null);
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Edit Limiter</DialogTitle>
                <DialogContent>
                    {editingLimiter && (
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                label="Name"
                                variant="outlined"
                                fullWidth
                                value={editingLimiter.name}
                                onChange={(e) =>
                                    setEditingLimiter({ ...editingLimiter, name: e.target.value })
                                }
                                sx={{ mb: 2 }}
                            />
                            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                                <InputLabel id="edit-limiter-type-label">Type</InputLabel>
                                <Select
                                    labelId="edit-limiter-type-label"
                                    value={editingLimiter.type}
                                    onChange={(e) =>
                                        setEditingLimiter({ ...editingLimiter, type: e.target.value })
                                    }
                                    label="Type"
                                >
                                    <MenuItem value="dropdown">Dropdown List</MenuItem>
                                    <MenuItem value="numeric">Numeric Field</MenuItem>
                                </Select>
                            </FormControl>
                            {editingLimiter.type === 'dropdown' && (
                                <TextField
                                    label="Values (comma separated)"
                                    variant="outlined"
                                    fullWidth
                                    value={editingLimiter.values.join(', ')}
                                    onChange={(e) =>
                                        setEditingLimiter({
                                            ...editingLimiter,
                                            values: e.target.value.split(',').map((v) => v.trim()),
                                        })
                                    }
                                    sx={{ mb: 2 }}
                                />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setEditDialogOpen(false);
                            setEditingLimiter(null);
                        }}
                        color="secondary"
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleEditSave} variant="contained" color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LimiterList;

